import { allowTabAccess } from './navigation-slice'
import { wrapCreateAsyncThunk } from 'app/app/(client)/utils/async-handler'
import { NavigationTab, NavigationTabList } from 'app/types/ui/grouping'
import { MAIN_TAB_LIST } from 'app/app/(client)/utils/client-constants'
import { OpportunityStatus } from 'app/types/ui/opportunity'
import { useNavigate } from 'app/app/(client)/hooks/useNavigate'
import { ClientFrontlineConfig, Offer } from 'app/types/types'
import { getOfferStatus } from 'app/app/(client)/utils/utils'
import { isPrepareInprogress } from 'app/app/(client)/utils/prepare-utils'
import { isGroupingInprogress } from 'app/app/(client)/utils/grouping-utils'
import { isOfferingInprogress } from 'app/app/(client)/utils/offer'
import { isContractEnabled } from 'app/app/(client)/utils/contract/contract-utils'

const getRouteByOpportunityStatus = (opportunityId: string, opportunityStatus: OpportunityStatus): string => {
  switch (opportunityStatus) {
    case OpportunityStatus.SentToSap:
      return `/${opportunityId}/contract`
    case OpportunityStatus.DiscountApprovalPending:
    case OpportunityStatus.OfferStatusIncomplete:
    case OpportunityStatus.DiscountApprovalRejected:
    case OpportunityStatus.OfferCreated:
      return `/${opportunityId}/offers`

    case OpportunityStatus.OfferInProgress:
      return `/${opportunityId}/offers/edit`

    case OpportunityStatus.GroupingInProgress:
      return `/${opportunityId}/grouping`

    case OpportunityStatus.PrepareInProgress:
    case OpportunityStatus.BeginPrepare:
    default:
      return `/${opportunityId}/prepare`
  }
}

const getTabAccessByOpportunityStatus = (opportunityStatus: OpportunityStatus): NavigationTab => {
  switch (opportunityStatus) {
    case OpportunityStatus.OfferCreated:
    case OpportunityStatus.SentToSap:
      return MAIN_TAB_LIST.contract

    case OpportunityStatus.DiscountApprovalPending:
    case OpportunityStatus.OfferStatusIncomplete:
    case OpportunityStatus.DiscountApprovalRejected:
    case OpportunityStatus.OfferInProgress:
      return MAIN_TAB_LIST.offers

    case OpportunityStatus.GroupingInProgress:
      return MAIN_TAB_LIST.grouping

    case OpportunityStatus.PrepareInProgress:
    case OpportunityStatus.BeginPrepare:
    default:
      return MAIN_TAB_LIST.prepare
  }
}

const getAllowedTabAccess = (
  offer: Offer,
  frontlineConfig: ClientFrontlineConfig,
  opportunityStatus: OpportunityStatus
): NavigationTab => {
  if (isPrepareInprogress(offer, frontlineConfig)) {
    return MAIN_TAB_LIST.prepare
  } else if (isGroupingInprogress(offer)) {
    return MAIN_TAB_LIST.grouping
  } else if (isOfferingInprogress(offer)) {
    return MAIN_TAB_LIST.offers
  } else if (isContractEnabled(offer)) {
    return MAIN_TAB_LIST.contract
  }

  return getTabAccessByOpportunityStatus(opportunityStatus)
}

export const updateTabDisabledStates = (tabList: NavigationTabList[], tabValue: NavigationTab): NavigationTabList[] => {
  const tabIndex = tabList.findIndex(tab => tab.value === tabValue)

  if (tabIndex === -1) {
    return tabList
  }

  return tabList.map((tab, index) => ({
    ...tab,
    isDisabled: index > tabIndex
  }))
}

export const updateTabAccess = wrapCreateAsyncThunk<OpportunityStatus, Offer>(
  'navigation/updateTabAccess',
  async (currentOffer, thunkAPI): Promise<OpportunityStatus> => {
    const { dispatch, getState } = thunkAPI

    const state = getState()
    const { data: configData } = state.configdata

    if (!currentOffer || !configData) {
      return OpportunityStatus.BeginPrepare
    }
    const { frontLineConfig } = configData
    if (!frontLineConfig) {
      return OpportunityStatus.BeginPrepare
    }

    const { status: opportunityStatus } = getOfferStatus(currentOffer, frontLineConfig)

    const tabAccess = getAllowedTabAccess(currentOffer, frontLineConfig, opportunityStatus)
    dispatch(allowTabAccess(tabAccess))

    return opportunityStatus
  }
)

interface NavigateToPagePayload {
  router: ReturnType<typeof useNavigate>
  offer: Offer
}

export const navigateToPage = wrapCreateAsyncThunk<void, NavigateToPagePayload>(
  'navigation/navigateToPage',
  async ({ router, offer }: NavigateToPagePayload, thunkAPI) => {
    const { dispatch } = thunkAPI

    const { opportunityId } = offer
    if (!opportunityId) {
      router.push('/')
      return
    }

    const opportunityStatus = await dispatch(updateTabAccess(offer)).unwrap()

    // If escalation flow is active AND offer is not finalized, navigate to contract renewal page
    // Once finalized, use normal routing to show versions page (tender versions)
    if (offer.isEscalationFlow?.active && !offer.finalized) {
      router.push(`/${opportunityId}/offers/contract-renewal`)
      return
    }

    const route = getRouteByOpportunityStatus(opportunityId, opportunityStatus)

    router.push(route)
  }
)
